import type { EventViewSportStatus } from "./model/EventView.graphql";

/**
 * The sports the Event View's Apollo shell can render, keyed on the BFF's `Sport.sportId`. An entry
 * mounts the shell only while its throttle is active; anything else is the untouched catalogue.
 * Which sports and why: `docs/specs/event-view-apollo-all-sports.md` §4.3.
 */
type EventViewSportEntry = {
  throttle: string;
  // True only when both `scoreboard-slots.{web,native}.tsx` fill it; their tests enforce that.
  scoreboard: boolean;
};

/** What the gate hands the shell: `sportId` picks the scoreboard slot, `entry` drives the rest. */
export type EventViewShell = { sportId: number; entry: EventViewSportEntry };

// The BFF's `SportsID` (tbd-bff `core-utils/src/sport-constants.ts`); tbd-store's `EventType` covers few.
export const SPORT_ID = {
  FOOTBALL: 1,
  TENNIS: 2,
  CRICKET: 4,
  RUGBY_UNION: 5,
  RUGBY_LEAGUE: 1477,
  DARTS: 3503,
  SNOOKER: 6422,
  AMERICAN_FOOTBALL: 6423,
  BASEBALL: 7511,
  BASKETBALL: 7522,
  ICE_HOCKEY: 7524,
  AUSTRALIAN_RULES: 61420,
  VOLLEYBALL: 998917,
  TABLE_TENNIS: 2593174,
} as const;

const shellOnly = (throttle: string): EventViewSportEntry => ({ throttle, scoreboard: false });

export const EVENT_VIEW_SPORTS: Readonly<Record<number, EventViewSportEntry>> = {
  [SPORT_ID.FOOTBALL]: { throttle: "FOOTBALL_EV_IMPROVEMENTS_HEADER", scoreboard: true },
  [SPORT_ID.AMERICAN_FOOTBALL]: shellOnly("APOLLO_EV_AMERICAN_FOOTBALL"),
  [SPORT_ID.AUSTRALIAN_RULES]: shellOnly("APOLLO_EV_AUSTRALIAN_RULES"),
  [SPORT_ID.BASEBALL]: shellOnly("APOLLO_EV_BASEBALL"),
  [SPORT_ID.BASKETBALL]: shellOnly("APOLLO_EV_BASKETBALL"),
  [SPORT_ID.DARTS]: shellOnly("APOLLO_EV_DARTS"),
  [SPORT_ID.ICE_HOCKEY]: shellOnly("APOLLO_EV_ICE_HOCKEY"),
  [SPORT_ID.RUGBY_LEAGUE]: shellOnly("APOLLO_EV_RUGBY_LEAGUE"),
  [SPORT_ID.RUGBY_UNION]: shellOnly("APOLLO_EV_RUGBY_UNION"),
  [SPORT_ID.SNOOKER]: shellOnly("APOLLO_EV_SNOOKER"),
  [SPORT_ID.TABLE_TENNIS]: shellOnly("APOLLO_EV_TABLE_TENNIS"),
  [SPORT_ID.VOLLEYBALL]: shellOnly("APOLLO_EV_VOLLEYBALL"),
  // BLOCKED until the Red7 work is done: never assign these two throttles (`event-view-apollo-all-sports.md` §13 O2).
  [SPORT_ID.TENNIS]: shellOnly("APOLLO_EV_TENNIS"),
  [SPORT_ID.CRICKET]: shellOnly("APOLLO_EV_CRICKET"),
};

/** No `noUncheckedIndexedAccess`, so an indexed read would type as always present. */
export const eventViewSportFor = (sportId: number): EventViewSportEntry | undefined =>
  Object.prototype.hasOwnProperty.call(EVENT_VIEW_SPORTS, sportId) ? EVENT_VIEW_SPORTS[sportId] : undefined;

export const REGISTERED_THROTTLES: readonly string[] = Object.values(EVENT_VIEW_SPORTS).map((entry) => entry.throttle);

/** With none on, the view resolves no sport at all (`docs/specs/event-view-apollo-all-sports.md` §4.4). */
export const isAnyEventViewSportOn = (activeThrottles: ReadonlySet<string>): boolean =>
  REGISTERED_THROTTLES.some((throttle) => activeThrottles.has(throttle));

/** Fails closed: only a resolved sport with an entry whose throttle is active gets the shell. */
export const eventViewShellFor = (
  sportStatus: EventViewSportStatus,
  activeThrottles: ReadonlySet<string>,
): EventViewShell | undefined => {
  if (typeof sportStatus !== "number") return undefined;
  const entry = eventViewSportFor(sportStatus);

  return entry && activeThrottles.has(entry.throttle) ? { sportId: sportStatus, entry } : undefined;
};

// Module-level constants: GenericView's map-to-props-factory memoises on the array's identity.
// Both switcher variants are listed, so the Apollo switcher replaces whichever comes back.
const SWITCHERS = ["GenericSwitcherCard", "EventTabNavigationCard"];
const SWITCHERS_AND_FIXTURE = [...SWITCHERS, "FixtureCard"];

/** The catalogue cards the shell renders itself; never goes in APOLLO_MIGRATED_CARDS. */
export const excludedCardTypes = (entry: EventViewSportEntry): string[] =>
  entry.scoreboard ? SWITCHERS_AND_FIXTURE : SWITCHERS;
